import axios from 'axios'
import { getSecureRefreshToken, saveRefreshToken } from '../helpers/secure-storage'

const INIT_DATA_KEY = 'gg_cached_initData'
function getCachedInitData() {
    const live = window.Telegram?.WebApp?.initData
    if (live && live.length > 20) return live
    try { return sessionStorage.getItem(INIT_DATA_KEY) || '' }
    catch (e) { return '' }
}

const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    (typeof window !== 'undefined' &&
    window.location &&
    window.location.hostname !== 'localhost'
        ? `${window.location.origin}/api`
        : 'http://localhost:5000/api')

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
})

export const setLang = (lang) => {
    axios.defaults.headers.common['X-localization'] = lang
    apiClient.defaults.headers.common['X-localization'] = lang
}

axios.defaults.headers.common.Accept = 'application/json'
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

apiClient.defaults.headers.common.Accept = 'application/json'
apiClient.defaults.headers.common['Content-Type'] = 'application/json'

export const setAuthToken = (token) => {
    if (token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
        delete apiClient.defaults.headers.common['Authorization']
    }
}

export const getAuthToken = () => {
    return localStorage.getItem('accessToken')
}

export const setTokens = (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    setAuthToken(accessToken)
}

export const clearTokens = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    setAuthToken(null)
}

const savedToken = getAuthToken()
if (savedToken) {
    setAuthToken(savedToken)
}

try {
    const tg = window.Telegram?.WebApp
    if (tg) {
        if (tg.platform) apiClient.defaults.headers.common['X-TG-Platform'] = tg.platform
        if (tg.version) apiClient.defaults.headers.common['X-TG-Version'] = tg.version
    }
} catch (e) { /* not in TG context */ }

apiClient.interceptors.request.use(
    (config) => {
        const token = getAuthToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

let _silentReauthInProgress = null

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true

            // Step 1: try refresh from localStorage
            try {
                const refreshToken = localStorage.getItem('refreshToken')
                if (refreshToken) {
                    const response = await axios.post(
                        `${API_BASE_URL}/auth/refresh`,
                        { refreshToken }
                    )
                    const { accessToken, refreshToken: newRefreshToken } =
                        response.data.tokens
                    setTokens(accessToken, newRefreshToken)
                    saveRefreshToken(newRefreshToken)
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`
                    return apiClient(originalRequest)
                }
            } catch (refreshError) {
                console.warn('Token refresh failed, trying SecureStorage...')
            }

            // Step 2: try refresh from SecureStorage (survives cache clears)
            if (!originalRequest._secureStorageTried) {
                originalRequest._secureStorageTried = true
                try {
                    const secureRT = await getSecureRefreshToken()
                    if (secureRT) {
                        const response = await axios.post(
                            `${API_BASE_URL}/auth/refresh`,
                            { refreshToken: secureRT }
                        )
                        const { accessToken, refreshToken: newRefreshToken } =
                            response.data.tokens
                        setTokens(accessToken, newRefreshToken)
                        saveRefreshToken(newRefreshToken)
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`
                        return apiClient(originalRequest)
                    }
                } catch (secureError) {
                    console.warn('SecureStorage refresh failed, trying initData...')
                }
            }

            // Step 3: try silent re-auth via cached initData
            if (!originalRequest._silentReauth) {
                originalRequest._silentReauth = true
                const initData = getCachedInitData()
                if (initData && initData.length > 0) {
                    try {
                        if (!_silentReauthInProgress) {
                            const savedSiteId = localStorage.getItem('userSiteId')
                            _silentReauthInProgress = axios.post(
                                `${API_BASE_URL}/auth/telegram`,
                                { initData, siteId: savedSiteId || undefined }
                            ).finally(() => { _silentReauthInProgress = null })
                        }

                        const reauthResponse = await _silentReauthInProgress
                        if (!reauthResponse.data.user?.siteId) {
                            throw new Error('No siteId after re-auth')
                        }

                        const { accessToken, refreshToken: newRefresh } = reauthResponse.data.tokens
                        setTokens(accessToken, newRefresh)
                        saveRefreshToken(newRefresh)
                        localStorage.setItem('userSiteId', reauthResponse.data.user.siteId)

                        originalRequest.headers.Authorization = `Bearer ${accessToken}`
                        return apiClient(originalRequest)
                    } catch (reauthError) {
                        console.error('Silent re-auth failed:', reauthError?.response?.data?.code || reauthError.message)
                    }
                }
            }

            clearTokens()
        }

        return Promise.reject(error)
    }
)

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        // if (error.response.status === 404) {
        //     toast('Not found', { closeButton: false, type: 'error' })
        // }

        return Promise.reject(error)
    }
)

export { apiClient }
export default axios
